import { PaletteOptions, Shadows, createTheme } from "@mui/material/styles";
import { buildComponents } from "./design/componentOverrides";
import { elevation, motion, radius, space } from "./design/tokens";
import { typography } from "./design/typography";

export type ConsoleMode = "light" | "dark";

/**
 * Console color tokens ("Varsity" light, "Mission Control" dark). Values and
 * contrast pairs: `specs/002-portal-design-theme/contracts/design-tokens.md`.
 *
 * Keep one `name: "#RRGGBB",` entry per line: `scripts/check-contrast.ts` parses this block as
 * text. Translucent (rgba) tokens and every non-color token live in `src/design/tokens.ts`.
 * Decorative only (never the sole carrier of text): `textFaint`, `border`, `gold`.
 *
 * Deviations from the contract table, measured with the WCAG 2.1 formula:
 * - light `inputBorder` #8690A8 (contract #8C96AD is 2.97:1 on white; this is 3.20:1).
 * - dark `onPrimary` #000000 (contract #050B1C on #3D6BFF is 4.43:1; this is 4.74:1).
 * - dark `danger` #DD3249 (contract #F0364F under white `onTier` is 3.93:1; this is 4.54:1).
 */
export const tokens = {
  light: {
    background: "#F5F7FC",
    surface: "#FFFFFF",
    surfaceAlt: "#EEF2FB",
    surfaceSunken: "#F8FAFD",
    border: "#E3E7F0",
    inputBorder: "#8690A8",
    text: "#0A1A3A",
    textMuted: "#56617A",
    textFaint: "#8C96AD",
    primary: "#0033A0",
    primaryInk: "#0033A0",
    primarySoft: "#EEF2FB",
    onPrimary: "#FFFFFF",
    strong: "#0A1A3A",
    onStrong: "#FFFFFF",
    danger: "#D0102C",
    dangerInk: "#C00E28",
    dangerSoft: "#FDECEE",
    tierPresidential: "#0033A0",
    tierNational: "#D0102C",
    tierParticipationBg: "#FFE7A3",
    tierParticipationFg: "#5B4300",
    onTier: "#FFFFFF",
    gold: "#F2B705",
    warningBg: "#FFF6DD",
    warningFg: "#5B4300",
    warningLine: "#F2C46B",
    success: "#1B7F4C",
    successBg: "#E3F4EA",
    successInk: "#166A3F",
    hero: "#0033A0",
    heroInk: "#FFFFFF",
    heroMuted: "#CFDAF5",
    inputBg: "#FFFFFF",
    track: "#E3E7F0",
    focusRing: "#0033A0",
  },
  dark: {
    background: "#050B1C",
    surface: "#0B1633",
    surfaceAlt: "#12214A",
    surfaceSunken: "#0F1D40",
    border: "#1D2B52",
    inputBorder: "#6B7BA3",
    text: "#EEF2FA",
    textMuted: "#9AA8C7",
    textFaint: "#6B7BA3",
    primary: "#3D6BFF",
    primaryInk: "#8FB0FF",
    primarySoft: "#14234D",
    onPrimary: "#000000",
    strong: "#F4F6FB",
    onStrong: "#050B1C",
    danger: "#DD3249",
    dangerInk: "#FF7B8C",
    dangerSoft: "#2A0F1A",
    tierPresidential: "#2F5BFF",
    tierNational: "#D92E46",
    tierParticipationBg: "#3B3212",
    tierParticipationFg: "#FFD66B",
    onTier: "#FFFFFF",
    gold: "#FFC94D",
    warningBg: "#1E1606",
    warningFg: "#FFC94D",
    warningLine: "#4A3A10",
    success: "#4ADE80",
    successBg: "#0E2A1C",
    successInk: "#4ADE80",
    hero: "#0B1633",
    heroInk: "#EEF2FA",
    heroMuted: "#9AA8C7",
    inputBg: "#0A1430",
    track: "#1A2850",
    focusRing: "#3D6BFF",
  },
} as const;

export type ColorTokenName = keyof (typeof tokens)["light"];
export type ColorTokens = Record<ColorTokenName, string>;

declare module "@mui/material/styles" {
  interface Palette {
    /** Every color token of the active mode, by token name (also emitted as `--pft-<name>`). */
    tokens: ColorTokens;
    surfaceAlt: string;
    surfaceSunken: string;
    inputBorder: string;
    inputBg: string;
    track: string;
    focusRing: string;
    primaryInk: string;
    strong: string;
    onStrong: string;
    dangerInk: string;
    dangerSoft: string;
    tierPresidential: string;
    tierNational: string;
    tierParticipationBg: string;
    tierParticipationFg: string;
    onTier: string;
    gold: string;
    warningLine: string;
    successInk: string;
    hero: string;
    heroInk: string;
    heroMuted: string;
  }
  interface PaletteOptions {
    tokens?: ColorTokens;
    surfaceAlt?: string;
    surfaceSunken?: string;
    inputBorder?: string;
    inputBg?: string;
    track?: string;
    focusRing?: string;
    primaryInk?: string;
    strong?: string;
    onStrong?: string;
    dangerInk?: string;
    dangerSoft?: string;
    tierPresidential?: string;
    tierNational?: string;
    tierParticipationBg?: string;
    tierParticipationFg?: string;
    onTier?: string;
    gold?: string;
    warningLine?: string;
    successInk?: string;
    hero?: string;
    heroInk?: string;
    heroMuted?: string;
  }
}

export const buildPalette = (mode: ConsoleMode): PaletteOptions => {
  const t: ColorTokens = tokens[mode];
  return {
    mode,
    tokens: t,
    primary: {
      light: t.primarySoft,
      main: t.primary,
      dark: t.primaryInk,
      contrastText: t.onPrimary,
    },
    secondary: {
      light: t.surfaceAlt,
      main: t.strong,
      dark: t.text,
      contrastText: t.onStrong,
    },
    error: {
      light: t.dangerSoft,
      main: t.danger,
      dark: t.dangerInk,
      contrastText: t.onTier,
    },
    warning: {
      light: t.warningBg,
      main: t.warningFg,
      dark: t.warningFg,
      contrastText: t.warningBg,
    },
    success: {
      light: t.successBg,
      main: t.success,
      dark: t.successInk,
      // White on `success` fails in dark (1.74:1), so dark fills use `onStrong`.
      contrastText: mode === "light" ? t.onTier : t.onStrong,
    },
    info: {
      light: t.primarySoft,
      main: t.primary,
      dark: t.primaryInk,
      contrastText: t.onPrimary,
    },
    background: { default: t.background, paper: t.surface },
    text: { primary: t.text, secondary: t.textMuted, disabled: t.textFaint },
    divider: t.border,
    surfaceAlt: t.surfaceAlt,
    surfaceSunken: t.surfaceSunken,
    inputBorder: t.inputBorder,
    inputBg: t.inputBg,
    track: t.track,
    focusRing: t.focusRing,
    primaryInk: t.primaryInk,
    strong: t.strong,
    onStrong: t.onStrong,
    dangerInk: t.dangerInk,
    dangerSoft: t.dangerSoft,
    tierPresidential: t.tierPresidential,
    tierNational: t.tierNational,
    tierParticipationBg: t.tierParticipationBg,
    tierParticipationFg: t.tierParticipationFg,
    onTier: t.onTier,
    gold: t.gold,
    warningLine: t.warningLine,
    successInk: t.successInk,
    hero: t.hero,
    heroInk: t.heroInk,
    heroMuted: t.heroMuted,
  };
};

export const palette: PaletteOptions = buildPalette("light");

/** MUI needs 25 levels: 0 none, 1–7 the card elevation, 8+ the menu/dialog elevation. */
const buildShadows = (mode: ConsoleMode): Shadows =>
  Array.from({ length: 25 }, (_, level) => {
    if (level === 0) return "none";
    return level < 8 ? elevation.card[mode] : elevation.menu;
  }) as Shadows;

/**
 * Composes the console theme. `reducedMotion` zeroes every transition duration (the global
 * `prefers-reduced-motion` CSS in `design/globalStyles.ts` covers non-MUI transitions).
 */
export const buildTheme = (mode: ConsoleMode, reducedMotion = false) => {
  const duration = (ms: number) => (reducedMotion ? 0 : ms);
  return createTheme({
    palette: buildPalette(mode),
    typography,
    components: buildComponents(mode, reducedMotion),
    shape: { borderRadius: radius.control },
    spacing: space,
    shadows: buildShadows(mode),
    transitions: {
      duration: {
        shortest: duration(motion.fast),
        shorter: duration(motion.fast),
        short: duration(motion.fast),
        standard: duration(motion.base),
        complex: duration(motion.slow),
        enteringScreen: duration(motion.base),
        leavingScreen: duration(motion.fast),
      },
    },
  });
};

/** Static light theme for the few remaining non-hook importers. Prefer `useTheme()`. */
const theme = buildTheme("light");
export default theme;
